import { useRouterState } from "@tanstack/react-router";
import * as React from "react";
import { cn } from "ui";

export function GlobalNavigationProgress() {
  const isLoading = useRouterState({
    select: (state) => {
      return state.isLoading;
    },
  });
  const pathname = useRouterState({
    select: (state) => {
      return state.location.pathname;
    },
  });
  const globalProgress = useRouterState({
    select: (state) => {
      return state.location.state.globalProgress ?? true;
    },
  });

  const ref = React.useRef<HTMLDivElement>(null);
  const [animationComplete, setAnimationComplete] = React.useState(true);
  const previousPathnameRef = React.useRef<string>(pathname);
  const [isRouteChanging, setIsRouteChanging] = React.useState(false);

  React.useEffect(() => {
    if (pathname !== previousPathnameRef.current) {
      setIsRouteChanging(true);
      previousPathnameRef.current = pathname;
    } else if (!isLoading && isRouteChanging) {
      setIsRouteChanging(false);
    }
  }, [pathname, isLoading, isRouteChanging]);

  React.useEffect(() => {
    if (!ref.current) return;

    const shouldShowProgress = isLoading && isRouteChanging;

    if (shouldShowProgress) {
      setAnimationComplete(false);
    } else {
      Promise.allSettled(
        ref.current.getAnimations().map(({ finished }) => {
          return finished;
        }),
      ).then(() => {
        setAnimationComplete(true);
      });
    }
  }, [isLoading, isRouteChanging]);

  if (!globalProgress) return null;

  const shouldShowProgress = isLoading && isRouteChanging;

  return (
    <div
      role="progressbar"
      aria-hidden={!shouldShowProgress}
      aria-valuetext={shouldShowProgress ? "Loading" : undefined}
      className="fixed inset-x-0 top-0 left-0 z-100 h-1 animate-pulse"
    >
      <div
        ref={ref}
        className={cn(
          "h-full z-[9999] bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500 ease-in-out w-0",
          globalProgress && {
            "w-0 opacity-0 transition-none":
              !shouldShowProgress && animationComplete,
            "w-10/12": shouldShowProgress,
            "w-full": !shouldShowProgress && !animationComplete,
          },
        )}
      />
    </div>
  );
}
